import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Send, Loader2, Mail, MessageSquare, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// v3.21-1775 · Client Link Delivery modal — Email / Text / Email + Text
// for the three canonical Deal-stage link types:
//   • credit_application   (App Link Sent stage)
//   • ach_disclosure       (Disclosure ACH Sent stage)
//   • appointment          (Appointment Scheduled stage — link only,
//                           does NOT flip the stage to Scheduled=Yes)
// The frontend NEVER supplies a URL.  The backend resolves the
// canonical URL from `link_type` and re-appends it on send.

const LINK_TYPE_META = {
  credit_application: {
    title: 'Send Credit Application Link',
    label: 'Credit Application Link',
    defaultBody: (name) =>
      `Hi ${name || 'there'}, here is your credit application link:\n\n[Credit Application Link]\n\nPlease complete the application when you have a moment.`,
  },
  ach_disclosure: {
    title: 'Send ACH / Disclosure Link',
    label: 'ACH / Disclosure Link',
    defaultBody: (name) =>
      `Hi ${name || 'there'}, here is your secure ACH / disclosure link:\n\n[ACH / Disclosure Link]\n\nPlease complete it when you have a moment.`,
  },
  appointment: {
    title: 'Send Appointment Link',
    label: 'Appointment Link',
    defaultBody: (name) =>
      `Hi ${name || 'there'}, here is your appointment scheduling link:\n\n[Appointment Link]\n\nPlease use the link to schedule your appointment.`,
  },
};

function maskPhone(p) {
  if (!p) return '—';
  const d = String(p).replace(/\D/g, '');
  if (d.length < 4) return p;
  return `(***) ***-${d.slice(-4)}`;
}
function maskEmail(e) {
  if (!e || !e.includes('@')) return e || '—';
  const [u, dom] = e.split('@');
  const uMask = u.length <= 1 ? u : u[0] + '***';
  return `${uMask}@${dom}`;
}

const LinkDeliveryModal = ({ open, onClose, linkType, client, clientId, onSent }) => {
  const meta = LINK_TYPE_META[linkType] || LINK_TYPE_META.credit_application;
  // v3.21-1775 §5 — default to Text Message.  Operator can always change.
  const [channel, setChannel] = useState('sms');
  const [reason, setReason] = useState('urgent_completion');
  const [reasonText, setReasonText] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      const first = (client?.first_name || (client?.full_name || '').split(' ')[0] || '');
      setChannel('sms');
      setReason('urgent_completion');
      setReasonText('');
      setMessage(meta.defaultBody(first));
      setResult(null);
    }
  }, [open, linkType]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasEmail = Boolean((client?.email || '').trim());
  const hasPhone = Boolean((client?.phone || '').trim());
  const emailChoiceDisabled = !hasEmail;
  const smsChoiceDisabled = !hasPhone;

  const channels =
    channel === 'both' ? ['email', 'sms']
    : channel === 'email' ? ['email']
    : ['sms'];

  const sendDisabled =
    sending
    || (channel === 'sms' && !hasPhone)
    || (channel === 'email' && !hasEmail)
    || (channel === 'both' && (!hasEmail || !hasPhone))
    || (reason === 'other' && !(reasonText || '').trim());

  const handleSend = async () => {
    if (sendDisabled) return;
    setSending(true);
    setResult(null);
    try {
      const resp = await api.post(`/clients/${clientId}/link-delivery/send`, {
        link_type: linkType,
        channels,
        message,
        reason_code: reason,
        reason_text: reasonText || null,
      });
      const r = resp.data || {};
      setResult(r);
      const emailAttempted = !!r?.email?.attempted;
      const smsAttempted   = !!r?.sms?.attempted;
      const emailOk = emailAttempted ? r.email.ok : null;
      const smsOk   = smsAttempted   ? r.sms.ok   : null;
      if (emailOk === true && smsOk === true) toast.success('Email and text message sent.');
      else if (smsOk === true && emailOk === false) toast.warning('Text message sent. Email delivery failed.');
      else if (emailOk === true && smsOk === false) toast.warning('Email sent. Text delivery failed.');
      else if (smsOk === true) toast.success('Text message sent.');
      else if (emailOk === true) toast.success('Email sent.');
      else toast.error('Delivery failed.');
      if (r.ok) { onSent?.(r); }
      // v3.21-1819 · Auto-close ONLY when every requested channel
      // delivered.  Partial-failure or full-failure keeps the modal
      // open so the operator can review, edit the message (React
      // useState preserves the textarea content across renders),
      // and retry without re-typing.  The Cancel button remains as
      // an explicit dismiss.
      const emailFullOk = !emailAttempted || emailOk === true;
      const smsFullOk   = !smsAttempted   || smsOk   === true;
      const anyAttempt  = emailAttempted || smsAttempted;
      const allChannelsOk = anyAttempt && emailFullOk && smsFullOk;
      if (r.ok && allChannelsOk) {
        onClose();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to send link');
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="link-delivery-modal">
        <DialogHeader>
          <DialogTitle data-testid="link-delivery-title">{meta.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <div className="text-[11px] text-slate-500">Client</div>
              <div className="font-medium" data-testid="link-delivery-client">{client?.full_name || '—'}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500">Mobile</div>
              <div className="font-mono text-xs" data-testid="link-delivery-mobile">
                {hasPhone ? maskPhone(client.phone) : <span className="text-red-600">No mobile</span>}
              </div>
            </div>
            <div className="col-span-2">
              <div className="text-[11px] text-slate-500">Email</div>
              <div className="text-xs" data-testid="link-delivery-email">
                {hasEmail ? maskEmail(client.email) : <span className="text-red-600">No email</span>}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">Delivery Method</Label>
            <div className="flex gap-4 pt-1 text-sm">
              <label className={`flex items-center gap-1.5 cursor-pointer ${emailChoiceDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="channel" value="email"
                  checked={channel === 'email'} disabled={emailChoiceDisabled}
                  onChange={() => setChannel('email')}
                  data-testid="link-delivery-channel-email"
                />
                <Mail className="h-3.5 w-3.5" /> Email
              </label>
              <label className={`flex items-center gap-1.5 cursor-pointer ${smsChoiceDisabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="channel" value="sms"
                  checked={channel === 'sms'} disabled={smsChoiceDisabled}
                  onChange={() => setChannel('sms')}
                  data-testid="link-delivery-channel-sms"
                />
                <MessageSquare className="h-3.5 w-3.5" /> Text Message
              </label>
              <label className={`flex items-center gap-1.5 cursor-pointer ${(emailChoiceDisabled || smsChoiceDisabled) ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="channel" value="both"
                  checked={channel === 'both'}
                  disabled={emailChoiceDisabled || smsChoiceDisabled}
                  onChange={() => setChannel('both')}
                  data-testid="link-delivery-channel-both"
                />
                Email + Text
              </label>
            </div>
          </div>

          <div>
            <Label className="text-xs">Message</Label>
            <Textarea
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="link-delivery-message"
            />
            <div className="text-[11px] text-slate-500 pt-1">
              Secure link is appended by the Hub on send — you do not need to paste a URL.
            </div>
          </div>

          <div className="text-[11px] text-slate-500 border-l-2 border-slate-200 pl-2">
            Link: <span className="font-medium">{meta.label}</span> (server-resolved)
          </div>

          {(channel === 'sms' || channel === 'both') && (
            <div className="border rounded p-2 bg-amber-50" data-testid="link-delivery-sms-reason">
              <Label className="text-xs">SMS Reason</Label>
              <select
                className="w-full h-8 text-sm border rounded px-1"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="link-delivery-reason"
              >
                <option value="urgent_completion">Urgent completion</option>
                <option value="client_cannot_find_email">Client cannot find email</option>
                <option value="client_cannot_access_spam_folder">Client cannot access spam folder</option>
                <option value="email_not_received">Email not received</option>
                <option value="other">Other</option>
              </select>
              {reason === 'other' && (
                <Input
                  className="mt-1"
                  placeholder="Reason detail (required for 'Other')"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  data-testid="link-delivery-reason-text"
                />
              )}
            </div>
          )}

          {result && (
            <div className="border rounded p-2 bg-slate-50 text-xs space-y-1" data-testid="link-delivery-result">
              {result.email?.attempted && (
                <div className="flex items-center gap-1.5">
                  {result.email.ok
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    : <AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
                  Email: {result.email.ok ? 'sent' : (result.email.error || 'failed')}
                </div>
              )}
              {result.sms?.attempted && (
                <div className="flex items-center gap-1.5">
                  {result.sms.ok
                    ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    : <AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
                  Text: {result.sms.ok ? (result.sms.outcome || 'sent') : (result.sms.error || 'failed')}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="link-delivery-cancel">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sendDisabled} data-testid="link-delivery-send">
            {sending
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Sending...</>
              : <><Send className="h-3.5 w-3.5 mr-1" /> Send</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkDeliveryModal;
